import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/hooks/useTheme';
import { useOrg } from '@/features/lc/OrgProvider';
import { navigateTo } from '@/features/lc/router';
import { LoginModal } from './LoginModal';
import type { Theme } from '@/types';

function AvatarButton({ initials, onClick }: { initials: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open account menu"
      aria-haspopup="menu"
      className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-semibold text-bg transition-colors hover:bg-ink/85"
    >
      {initials}
    </button>
  );
}

function GuestButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Sign in"
      className="flex h-8 w-8 items-center justify-center rounded-full border border-rule text-dim transition-colors hover:border-accent hover:text-accent"
    >
      <svg
        aria-hidden="true"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
        />
      </svg>
    </button>
  );
}

function GuestTextButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center justify-center rounded-sm border border-ink bg-ink px-4 text-sm font-semibold text-bg transition-colors hover:bg-ink/85"
    >
      {label}
    </button>
  );
}

const themeOptions: { value: Theme; label: string; icon: React.ReactNode }[] = [
  {
    value: 'light',
    label: 'Light',
    icon: (
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <circle cx="12" cy="12" r="4" />
        <path
          strokeLinecap="round"
          d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
        />
      </svg>
    ),
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: (
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
        />
      </svg>
    ),
  },
  {
    value: 'system',
    label: 'System',
    icon: (
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <path strokeLinecap="round" d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
];

export function UserMenu({ guestDisplay = 'icon' }: { guestDisplay?: 'icon' | 'button' }) {
  const { user, isLoading: authLoading, isConfigured, mode, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { orgs, activeOrg, setActiveOrg, isLoading: orgLoading } = useOrg();
  const [open, setOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Wait for the initial /api/me probe before deciding what to render —
  // otherwise the sign-in button flashes on for authed users on reload.
  if (authLoading) return null;

  // Supabase mode with missing VITE_SUPABASE_* creds: hide the menu
  // entirely so we don't offer a login that can't complete. In basic
  // mode isConfigured is always true.
  if (mode === 'supabase' && !isConfigured) return null;

  const initials = user?.email ? user.email.substring(0, 2).toUpperCase() : '';

  return (
    <div ref={ref} className="relative">
      {user ? (
        <AvatarButton initials={initials} onClick={() => setOpen(!open)} />
      ) : guestDisplay === 'button' ? (
        <GuestTextButton label="Sign in" onClick={() => setShowLogin(true)} />
      ) : (
        <GuestButton onClick={() => setShowLogin(true)} />
      )}

      {open && user && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 border border-rule bg-panel shadow-lg">
          {/* User info */}
          <div className="border-b border-rule-soft px-4 py-3">
            <p className="text-sm font-medium text-ink truncate">{user.email}</p>
          </div>

          {/* Organization section */}
          {!orgLoading && orgs.length > 0 && (
            <div className="border-b border-rule-soft py-1">
              <p className="px-4 py-1.5 text-2xs font-medium uppercase tracking-wider text-dim">
                Organization
              </p>
              {orgs.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => {
                    setActiveOrg(org);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors ${
                    activeOrg?.id === org.id ? 'bg-accent-soft text-accent' : 'text-ink hover:bg-bg'
                  }`}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-bg text-3xs font-medium text-dim">
                    {org.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="truncate">{org.name}</span>
                  {activeOrg?.id === org.id && (
                    <svg
                      className="ml-auto h-3.5 w-3.5 text-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigateTo('/org/new');
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-dim hover:bg-bg transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                New organization
              </button>
              {activeOrg && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigateTo('/org/settings');
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-dim hover:bg-bg transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Org settings
                </button>
              )}
            </div>
          )}

          {/* No orgs — prompt to create */}
          {!orgLoading && orgs.length === 0 && (
            <div className="border-b border-rule-soft py-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigateTo('/org/new');
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-accent hover:bg-accent-soft transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create organization
              </button>
            </div>
          )}

          {/* Theme */}
          <div className="border-b border-rule-soft py-1">
            <p className="px-4 py-1.5 text-2xs font-medium uppercase tracking-wider text-dim">
              Theme
            </p>
            <div className="flex gap-1 px-4 py-1.5">
              {themeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    theme === opt.value
                      ? 'bg-accent-soft text-accent'
                      : 'text-dim hover:bg-bg hover:text-ink'
                  }`}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigateTo('/docs');
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-ink hover:bg-bg transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                />
              </svg>
              Docs
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void signOut();
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-del hover:bg-del/10 transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
                />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </div>
  );
}
